<template>
  <div class="vehicle-detail-page">
    <!-- Top sticky action bar -->
    <div class="action-bar">
      <button class="link-btn" @click="onGoBack" type="button">
        <i class="fas fa-arrow-left me-2"></i>
        <span class="d-none d-sm-inline">Pricelist</span>
      </button>
      <div class="action-bar-title d-none d-md-block">
        {{ getBrandName() }} {{ getModelName() }}
      </div>
      <div class="action-bar-actions">
        <!-- Share menu: WhatsApp, Email, Copy Link. Closed on outside click. -->
        <div class="share-wrap" v-click-outside="closeShareMenu">
          <button
            class="action-btn action-btn-secondary"
            type="button"
            :aria-expanded="shareMenuOpen ? 'true' : 'false'"
            aria-haspopup="menu"
            @click="toggleShareMenu"
          >
            <i class="fas fa-share-alt me-2"></i>
            <span class="d-none d-sm-inline">Share</span>
          </button>
          <div v-if="shareMenuOpen" class="share-menu" role="menu">
            <a
              class="share-item"
              role="menuitem"
              :href="shareWhatsAppHref"
              target="_blank"
              rel="noopener noreferrer"
              @click="closeShareMenu"
            >
              <i class="fab fa-whatsapp share-item-icon share-item-icon-wa"></i>
              <span>WhatsApp</span>
            </a>
            <a
              class="share-item"
              role="menuitem"
              :href="shareEmailHref"
              @click="closeShareMenu"
            >
              <i class="fas fa-envelope share-item-icon"></i>
              <span>Email</span>
            </a>
            <button
              class="share-item"
              role="menuitem"
              type="button"
              @click="onCopyLink"
            >
              <i :class="copiedField === 'link' ? 'fas fa-check share-item-icon share-item-icon-success' : 'fas fa-link share-item-icon'"></i>
              <span>{{ copiedField === 'link' ? 'Link copied' : 'Copy link' }}</span>
            </button>
          </div>
        </div>
        <button class="action-btn" @click="onPrint" type="button">
          <i class="fas fa-print me-2"></i>
          <span class="d-none d-sm-inline">Print / Save as PDF</span>
          <span class="d-inline d-sm-none">Print</span>
        </button>
      </div>
    </div>

    <!-- Main 2-column layout -->
    <div class="main-layout">
      <!-- LEFT COLUMN — content -->
      <div class="main-content">
        <!-- Mobile title block (shown only when right aside is below the fold) -->
        <div class="mobile-header d-lg-none">
          <div class="status-pills">
            <CBadge
              v-for="(state, i) in stockStates"
              :key="i"
              :color="state.color"
              class="status-pill"
            >
              <i v-if="state.icon" :class="['fas', state.icon, 'me-1']"></i>{{ state.label }}
            </CBadge>
          </div>
          <span class="stock-no-inline">#{{ stock.stockNo }}</span>
          <h1 class="vehicle-title-mobile">{{ getBrandName() }} {{ getModelName() }}</h1>
          <div class="vehicle-meta">
            <span>{{ getVehicleYear() }}</span>
            <span class="meta-dot">·</span>
            <span>{{ stock.vehicle.color || '—' }}</span>
            <span class="meta-dot">·</span>
            <span>{{ getVehicleType() }}</span>
          </div>
        </div>

        <!-- Hero photo -->
        <div class="hero-section">
          <div
            v-if="heroPhoto"
            class="hero-image-wrapper"
            @click="showImageModal(heroPhoto)"
            @touchstart="handleHeroTouchStart"
            @touchend="handleHeroTouchEnd"
          >
            <img
              :src="getImageUrl(heroPhoto, 1200)"
              class="hero-image"
              alt="Vehicle photo"
              loading="eager"
              decoding="async"
            />
            <div class="hero-overlay">
              <i class="fas fa-search-plus"></i>
              <span class="d-none d-sm-inline ms-2">View all {{ totalImages }} photos</span>
              <span class="d-inline d-sm-none ms-1">{{ totalImages }} photos</span>
            </div>
          </div>
          <div v-else class="hero-placeholder">
            <i class="fas fa-image"></i>
            <p>No image available</p>
          </div>
        </div>

        <!-- Thumbnail strip -->
        <div class="thumbnail-strip" v-if="thumbPhotos.length > 0">
          <div
            v-for="(item, index) in thumbPhotos"
            :key="item.id"
            class="thumb-item"
            @click="showImageModal(item)"
          >
            <img
              :src="getImageUrl(item)"
              :alt="`Photo ${index + 1}`"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>

        <!-- Vehicle Specs grid -->
        <section class="content-section">
          <h2 class="section-heading">Vehicle Specs</h2>
          <div class="specs-grid">
            <div class="spec-tile">
              <i class="fas fa-cog spec-icon"></i>
              <div class="spec-content">
                <div class="spec-label">Engine</div>
                <div class="spec-value">{{ stock.vehicle.engineCapacity || '—' }}</div>
              </div>
            </div>
            <div class="spec-tile">
              <i class="fas fa-calendar-alt spec-icon"></i>
              <div class="spec-content">
                <div class="spec-label">Year / Month</div>
                <div class="spec-value">{{ getVehicleYear() }}<span v-if="getVehicleMonth() !== 'N/A'"> / {{ getVehicleMonth() }}</span></div>
              </div>
            </div>
            <div class="spec-tile">
              <i class="fas fa-palette spec-icon"></i>
              <div class="spec-content">
                <div class="spec-label">Color</div>
                <div class="spec-value">{{ stock.vehicle.color || '—' }}</div>
              </div>
            </div>
            <div class="spec-tile">
              <i class="fas fa-car spec-icon"></i>
              <div class="spec-content">
                <div class="spec-label">Vehicle Type</div>
                <div class="spec-value">{{ getVehicleType() }}</div>
              </div>
            </div>
            <div class="spec-tile spec-tile-wide">
              <i class="fas fa-id-card spec-icon"></i>
              <div class="spec-content">
                <div class="spec-label">Chassis No</div>
                <div class="spec-value-row">
                  <div class="spec-value spec-value-mono">{{ stock.vehicle.chasisNo || '—' }}</div>
                  <button
                    v-if="stock.vehicle.chasisNo"
                    type="button"
                    class="copy-btn"
                    :class="{ 'copy-btn-success': copiedField === 'chasis' }"
                    :title="copiedField === 'chasis' ? 'Copied' : 'Copy chassis number'"
                    @click.stop="copyToClipboard(stock.vehicle.chasisNo, 'chasis')"
                  >
                    <i :class="copiedField === 'chasis' ? 'fas fa-check' : 'fas fa-copy'"></i>
                  </button>
                </div>
              </div>
            </div>
            <div class="spec-tile">
              <i class="fas fa-ship spec-icon"></i>
              <div class="spec-content">
                <div class="spec-label">ETA</div>
                <div class="spec-value">{{ getEtaDate() }}</div>
              </div>
            </div>
          </div>
        </section>

        <!-- Description — rendered as a paragraph (preserves the agent's
             original formatting and capitalisation). Earlier we chip-ified
             comma-separated descriptions, but that lost the natural prose
             flow agents intentionally write. -->
        <section class="content-section" v-if="stock.vehicle.description">
          <h2 class="section-heading">Description</h2>
          <p class="description-text" v-html="formatAddress(stock.vehicle.description)"></p>
          <a
            v-if="stock.vehicle.externalLink"
            :href="stock.vehicle.externalLink"
            target="_blank"
            rel="noopener noreferrer"
            class="ext-link"
          >
            <i class="fas fa-external-link-alt me-2"></i>Google Drive Photos
          </a>
        </section>

        <!-- Remarks (reusable widget — composer disabled, delete-own enabled) -->
        <section class="content-section content-section-remarks">
          <WidgetsRemarks
            v-if="stock && stock.id"
            entity-type="stock"
            :entity-id="stock.id"
            :remarks-list="stock.remarksList || []"
            :is-read-only="true"
            @deleted="$emit('refresh-stock')"
            @toast="(t) => $emit('toast', t)"
          />
        </section>
      </div>

      <!-- RIGHT STICKY COLUMN (desktop only) -->
      <aside class="info-aside d-none d-lg-block">
        <div class="info-card">
          <div class="status-pills">
            <CBadge
              v-for="(state, i) in stockStates"
              :key="i"
              :color="state.color"
              class="status-pill status-pill-large"
            >
              <i v-if="state.icon" :class="['fas', state.icon, 'me-1']"></i>{{ state.label }}
            </CBadge>
          </div>
          <div class="stock-no-row">
            <span class="stock-no-label">STOCK NO</span>
            <span class="stock-no-value">#{{ stock.stockNo }}</span>
          </div>
          <h1 class="vehicle-title">{{ getBrandName() }} {{ getModelName() }}</h1>
          <div class="vehicle-meta">
            <span>{{ getVehicleYear() }}</span>
            <span class="meta-dot">·</span>
            <span>{{ stock.vehicle.color || '—' }}</span>
            <span class="meta-dot">·</span>
            <span>{{ getVehicleType() }}</span>
          </div>

          <div class="price-block">
            <div class="price-label">Recommended Price</div>
            <div class="price-recommended">{{ formatPrice(stock.pricing.recommendedSalePrice) }}</div>
            <div class="price-min-row">
              <span class="price-min-label">Min</span>
              <span class="price-min-value">{{ formatPrice(stock.pricing.minimumSalePrice) }}</span>
            </div>
          </div>

          <button class="primary-btn" @click="onPrint" type="button">
            <i class="fas fa-print me-2"></i>Print / Save as PDF
          </button>

          <div class="info-meta" v-if="daysInStock !== null || stock.recordDate">
            <div class="meta-row" v-if="daysInStock !== null">
              <span class="meta-label">Days in stock</span>
              <span class="meta-value">{{ daysInStock }}</span>
            </div>
            <div class="meta-row" v-if="stock.recordDate">
              <span class="meta-label">Added</span>
              <span class="meta-value">{{ formatShortDate(stock.recordDate) }}</span>
            </div>
          </div>
        </div>
      </aside>
    </div>

    <!-- Mobile sticky bottom price bar -->
    <div class="mobile-price-bar d-lg-none">
      <div class="mpb-price">
        <div class="mpb-amount">{{ formatPrice(stock.pricing.recommendedSalePrice) }}</div>
        <div class="mpb-min">Min: {{ formatPrice(stock.pricing.minimumSalePrice) }}</div>
      </div>
      <button class="mpb-btn" @click="onPrint" type="button">
        <i class="fas fa-print me-2"></i>Print
      </button>
    </div>

    <!-- Lightbox modal — fullscreen photo viewer.
         Design notes:
           - size="xl" + custom dark theme for cinematic photo viewing.
           - The default CModal title bar + Cancel/OK footer are suppressed
             via custom header slot + empty footer slot. The default footer
             buttons made no sense for an image viewer.
           - Body is dark (#0b0b0d) so the image is the focal point.
           - Header carries vehicle title + photo counter + download +
             close on one row.
           - Thumbnail strip at the bottom lets users jump directly to any
             photo (previously they had to step one-at-a-time). -->
    <CModal
      :show.sync="isImageModalVisible"
      size="xl"
      centered
      @close="closeImageModal"
      class="image-modal image-modal-dark"
    >
      <!-- Custom dark header -->
      <template #header>
        <div class="lightbox-header">
          <div class="lightbox-header-title">
            <div class="lightbox-vehicle">{{ getBrandName() }} {{ getModelName() }}</div>
            <div class="lightbox-counter-chip">
              <i class="fas fa-images me-2"></i>
              Photo {{ currentImageIndex + 1 }} of {{ totalImages }}
            </div>
          </div>
          <div class="lightbox-header-actions">
            <a
              v-if="currentFullSizeUrl"
              :href="currentFullSizeUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="lightbox-icon-btn"
              title="Open full size in new tab"
              aria-label="Open full size"
            >
              <i class="fas fa-external-link-alt"></i>
            </a>
            <button
              type="button"
              class="lightbox-icon-btn"
              title="Close (Esc)"
              aria-label="Close"
              @click="closeImageModal"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
      </template>

      <div
        class="modal-image-container"
        @touchstart="handleTouchStart"
        @touchend="handleTouchEnd"
      >
        <button
          v-if="hasPreviousImage"
          @click="previousImage"
          class="nav-button nav-button-prev"
          aria-label="Previous image"
          type="button"
        >
          <i class="fas fa-chevron-left"></i>
        </button>

        <!-- We deliberately do NOT use :key on the <img>. Reusing the same
             element means the old image stays visible while the browser
             fetches the new src — that, plus the dim+spinner overlay,
             gives the user clear "loading the next image" feedback instead
             of the previous behaviour where the counter advanced but the
             image looked static for a few seconds. -->
        <img
          v-if="currentImageUrl"
          ref="lightboxImg"
          :src="currentImageUrl"
          class="maximized-image"
          :class="{ loading: imageLoading }"
          alt="Vehicle Image"
          @load="onImageLoaded"
          @error="onImageLoadError"
        />
        <div v-else class="image-loading">
          <p>Loading image...</p>
        </div>

        <!-- Loading overlay: visible while a navigation is in flight. -->
        <div v-if="imageLoading" class="image-loading-overlay" aria-live="polite">
          <CSpinner color="light" size="lg" />
        </div>

        <button
          v-if="hasNextImage"
          @click="nextImage"
          class="nav-button nav-button-next"
          aria-label="Next image"
          type="button"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>

      <!-- Thumbnail filmstrip: click to jump directly to any photo. Hidden
           when only one photo (no point). Active thumb has a brand-blue ring. -->
      <div v-if="totalImages > 1" class="lightbox-strip" ref="lightboxStrip">
        <button
          v-for="(item, idx) in photoList"
          :key="item.id || idx"
          type="button"
          class="lightbox-thumb"
          :class="{ 'lightbox-thumb-active': idx === currentImageIndex }"
          :aria-label="`Jump to photo ${idx + 1}`"
          :aria-current="idx === currentImageIndex ? 'true' : 'false'"
          @click="jumpToImage(idx)"
          :ref="idx === currentImageIndex ? 'activeThumb' : null"
        >
          <img :src="getImageUrl(item, 128)" :alt="`Photo ${idx + 1}`" loading="lazy" />
        </button>
      </div>

      <!-- Suppress the default Cancel / OK footer from CModal — they were
           never meaningful actions for a photo viewer. -->
      <template #footer><span></span></template>
    </CModal>
  </div>
</template>

<script>
import moment from "moment";
import { formatMalaysiaDate } from "@/utils/dateUtil";
import WidgetsRemarks from "./WidgetsRemarks";

export default {
  name: "WidgetsVehicleInfo",
  components: {
    WidgetsRemarks,
  },
  directives: {
    // Local v-click-outside directive: invokes the bound function when the
    // user clicks anywhere outside the element. Used for the Share menu.
    "click-outside": {
      bind(el, binding) {
        el._clickOutsideHandler = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.addEventListener("click", el._clickOutsideHandler);
      },
      unbind(el) {
        document.removeEventListener("click", el._clickOutsideHandler);
        delete el._clickOutsideHandler;
      },
    },
  },
  props: {
    stock: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isImageModalVisible: false,
      currentImageIndex: 0,
      touchStartX: 0,
      touchEndX: 0,
      imageLoading: false,
      // Tracks URLs already prefetched into the browser cache so we don't
      // re-issue Image() prefetches on every navigation. Set lives for the
      // lifetime of the component.
      preloadedUrls: new Set(),
      // Timer used by setImageLoading to debounce the loading spinner so it
      // never flickers on cached images (which load in <150ms).
      _loadingTimer: null,
      // Which field is currently in "copied" feedback state (e.g. 'chasis',
      // 'link'). Reset by a timer 1.5s after each copy so the icon flips
      // back to the default clipboard glyph.
      copiedField: null,
      _copiedTimer: null,
      shareMenuOpen: false,
    };
  },
  computed: {
    photoList() {
      try {
        return this.stock.vehicle.vehiclePhotoList || [];
      } catch (e) {
        return [];
      }
    },
    defaultPhoto() {
      // Prefer the user-chosen DefaultImageUrl photo. Format /Documents/File/{docId}.
      const url = this.stock && this.stock.vehicle && this.stock.vehicle.defaultImageUrl;
      if (url) {
        const match = /\/Documents\/File\/([0-9a-fA-F-]{36})/i.exec(url);
        if (match) {
          return this.photoList.find(p => p.documentId === match[1]) || null;
        }
      }
      return null;
    },
    heroPhoto() {
      return this.defaultPhoto || this.photoList[0] || null;
    },
    thumbPhotos() {
      // All photos EXCEPT the hero; if no hero (no photos), empty array
      if (!this.heroPhoto) return [];
      return this.photoList.filter(p => p.documentId !== this.heroPhoto.documentId);
    },
    totalImages() {
      return this.photoList.length;
    },
    currentImageUrl() {
      // Use the thumbnail endpoint at 1600px instead of the full-resolution
      // original. Lightbox display area is at most ~1200px wide and 70vh
      // tall, so 1600px is sufficient and downloads in a fraction of the
      // time of a multi-MB camera original. The currentFullSizeUrl below
      // is used by the "Open full size" link for users who want the
      // un-downsized file.
      if (this.photoList.length > 0 &&
          this.currentImageIndex >= 0 &&
          this.currentImageIndex < this.photoList.length) {
        return this.getImageUrl(this.photoList[this.currentImageIndex], 1200);
      }
      return null;
    },
    currentFullSizeUrl() {
      if (this.photoList.length > 0 &&
          this.currentImageIndex >= 0 &&
          this.currentImageIndex < this.photoList.length) {
        return this.getFullImageUrl(this.photoList[this.currentImageIndex]);
      }
      return null;
    },
    hasPreviousImage() {
      return this.currentImageIndex > 0;
    },
    hasNextImage() {
      return this.currentImageIndex < this.totalImages - 1;
    },
    shareSummary() {
      // Plaintext used in WhatsApp/Email share. Includes the bare
      // essentials a buyer would ask first: brand+model, year, price,
      // stock no, then a deep link back to this page.
      const lines = [];
      const title = `${this.getBrandName()} ${this.getModelName()}`.trim();
      if (title) lines.push(title);
      const meta = [this.getVehicleYear(), this.stock.vehicle && this.stock.vehicle.color, this.getVehicleType()].filter(v => v && v !== "N/A" && v !== "Unknown").join(" · ");
      if (meta) lines.push(meta);
      const price = this.stock.pricing && this.stock.pricing.recommendedSalePrice;
      if (price) lines.push(`Price: ${this.formatPrice(price)}`);
      if (this.stock.stockNo) lines.push(`Stock #${this.stock.stockNo}`);
      lines.push(window.location.href);
      return lines.join("\n");
    },
    shareWhatsAppHref() {
      // wa.me works on mobile (deep-links the WhatsApp app) and desktop
      // (opens WhatsApp Web). Text MUST be percent-encoded.
      return `https://wa.me/?text=${encodeURIComponent(this.shareSummary)}`;
    },
    shareEmailHref() {
      const subject = `${this.getBrandName()} ${this.getModelName()} — ${this.stock.stockNo || ''}`.trim();
      return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(this.shareSummary)}`;
    },
    stockStates() {
      // Operational state pills shown on the detail page. Source of truth
      // is the same set of boolean flags used by VehicleList — so a vehicle
      // never disagrees about whether it's "Available" vs "Booked" between
      // the two views. A stock can carry multiple states simultaneously
      // (e.g. Available + LOU), which is why this returns an array.
      const s = this.stock || {};
      const out = [];
      if (s.isOpen)        out.push({ label: "Available",    color: "success",   icon: "fa-check-circle" });
      if (s.isLou)         out.push({ label: "LOU",          color: "info",      icon: "fa-file-contract" });
      if (s.isBooked)      out.push({ label: "Booked",       color: "warning",   icon: "fa-calendar-check" });
      if (s.isSold)        out.push({ label: "Sold",         color: "danger",    icon: "fa-handshake" });
      if (s.isCancelled)   out.push({ label: "Cancelled",    color: "secondary", icon: "fa-times-circle" });
      if (s.isRequestDuty) out.push({ label: "Request Duty", color: "primary",   icon: "fa-flag" });
      // Fallback for records with no operational flag set yet (rare —
      // usually pre-arrival drafts): surface the workflow status from
      // latestStockStatus so the page never renders a status-less card.
      if (out.length === 0) {
        out.push({ label: this.getLatestStockStatus(), color: this.getStatusColor(), icon: null });
      }
      return out;
    },
    daysInStock() {
      // Use movedToStockAt if available, else recordDate
      const ref = this.stock && (this.stock.movedToStockAt || this.stock.recordDate);
      if (!ref) return null;
      const d = moment(ref);
      if (!d.isValid()) return null;
      return Math.max(0, moment().diff(d, "days"));
    },
  },
  methods: {
    formatAddress(address) {
      try {
        return address.replace(/\n/g, "<br />");
      } catch (error) {
        return address;
      }
    },
    showImageModal(photo) {
      const index = this.photoList.findIndex(p => p.documentId === photo.documentId);
      this.currentImageIndex = index >= 0 ? index : 0;
      this.isImageModalVisible = true;
      this.setImageLoading(true);
      this.preloadNeighbors();
    },
    closeImageModal() {
      this.isImageModalVisible = false;
      this.currentImageIndex = 0;
      this.setImageLoading(false);
    },
    nextImage() {
      if (this.hasNextImage) {
        this.currentImageIndex++;
        this.setImageLoading(true);
        this.preloadNeighbors();
        this.$nextTick(this.scrollActiveThumbIntoView);
      }
    },
    previousImage() {
      if (this.hasPreviousImage) {
        this.currentImageIndex--;
        this.setImageLoading(true);
        this.preloadNeighbors();
        this.$nextTick(this.scrollActiveThumbIntoView);
      }
    },
    setImageLoading(loading) {
      // Spinner debouncer: don't paint the loading state until 200ms has
      // passed. Cached images load within one frame (~16ms), so they never
      // trigger the visible spinner. Uncached images (first open, slow
      // network) surface the spinner smoothly after the debounce.
      if (this._loadingTimer) {
        clearTimeout(this._loadingTimer);
        this._loadingTimer = null;
      }
      if (loading) {
        this._loadingTimer = setTimeout(() => {
          // Last-chance check: by the time the debounce expires, the
          // browser may have already finished loading the image (e.g.
          // it was in the disk cache). Vue's @load can race with the
          // listener attachment for cached images, so it's possible
          // onImageLoaded never fired even though the image is ready.
          // Reading naturalWidth proves the image bytes are decoded.
          const img = this.$refs.lightboxImg;
          if (img && img.complete && img.naturalWidth > 0) {
            this.imageLoading = false;
            this._loadingTimer = null;
            return;
          }
          this.imageLoading = true;
          this._loadingTimer = null;
        }, 200);
      } else {
        this.imageLoading = false;
      }
    },
    jumpToImage(idx) {
      if (idx === this.currentImageIndex || idx < 0 || idx >= this.totalImages) return;
      this.currentImageIndex = idx;
      this.setImageLoading(true);
      this.preloadNeighbors();
      this.$nextTick(this.scrollActiveThumbIntoView);
    },
    scrollActiveThumbIntoView() {
      // Keep the active thumbnail visible when the strip overflows on
      // wide galleries (e.g. 20+ photos). scroll-behavior: smooth gives
      // the cue that "the strip moved with you."
      const ref = this.$refs.activeThumb;
      const el = Array.isArray(ref) ? ref[0] : ref;
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
      }
    },
    onImageLoaded() {
      this.setImageLoading(false);
      // The loaded URL is now in the browser cache; record it so we can
      // (cheaply) skip the prefetch later.
      const url = this.getImageUrl(this.photoList[this.currentImageIndex], 1200);
      if (url) this.preloadedUrls.add(url);
    },
    onImageLoadError() {
      // Surface the broken state instead of staying stuck on the spinner.
      this.setImageLoading(false);
    },
    preloadNeighbors() {
      // Prefetch ±1 around the current index using the SAME 1600px URL
      // the lightbox displays — so the browser cache hit on the next click
      // is for the actual URL that will be rendered, not a different one.
      const indices = [this.currentImageIndex - 1, this.currentImageIndex + 1];
      indices.forEach((i) => {
        if (i < 0 || i >= this.totalImages) return;
        const url = this.getImageUrl(this.photoList[i], 1200);
        if (!url || this.preloadedUrls.has(url)) return;
        const img = new Image();
        img.onload = () => this.preloadedUrls.add(url);
        // onerror is ignored — main view will surface the failure if the
        // user navigates there.
        img.src = url;
      });
    },
    handleKeydown(event) {
      if (!this.isImageModalVisible) return;
      if (event.key === "ArrowRight") this.nextImage();
      else if (event.key === "ArrowLeft") this.previousImage();
      else if (event.key === "Escape") this.closeImageModal();
    },
    handleTouchStart(event) {
      this.touchStartX = event.changedTouches[0].screenX;
    },
    handleTouchEnd(event) {
      this.touchEndX = event.changedTouches[0].screenX;
      this.handleSwipe();
    },
    handleSwipe() {
      const swipeThreshold = 50;
      const diff = this.touchStartX - this.touchEndX;
      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) this.nextImage();
        else this.previousImage();
      }
    },
    handleHeroTouchStart(event) {
      this.touchStartX = event.changedTouches[0].screenX;
    },
    handleHeroTouchEnd(event) {
      this.touchEndX = event.changedTouches[0].screenX;
      const diff = this.touchStartX - this.touchEndX;
      // Only treat as swipe if movement is large enough; otherwise let the click event handle it
      if (Math.abs(diff) > 80) {
        event.preventDefault();
        if (diff > 0) {
          // Swipe left — open the lightbox and advance once
          this.showImageModal(this.heroPhoto);
          this.$nextTick(() => this.nextImage());
        } else if (this.totalImages > 1) {
          this.showImageModal(this.heroPhoto);
          this.$nextTick(() => this.previousImage());
        }
      }
    },
    getStatusColor() {
      const status = this.getLatestStockStatus().toLowerCase();
      if (status.includes("available") || status.includes("ready")) return "success";
      if (status.includes("sold") || status.includes("reserved")) return "danger";
      if (status.includes("pending")) return "warning";
      return "secondary";
    },
    getVehicleMonth() {
      try { return this.stock.vehicle.month || "N/A"; } catch (e) { return "N/A"; }
    },
    getVehicleYear() {
      try { return this.stock.vehicle.year || "N/A"; } catch (e) { return "N/A"; }
    },
    getVehicleType() {
      try { return this.stock.vehicle.vehicleType.name || "N/A"; } catch (e) { return "N/A"; }
    },
    getBrandName() {
      try { return this.stock.vehicle.brand.name || "Unknown"; } catch (e) { return "Unknown"; }
    },
    getModelName() {
      try { return this.stock.vehicle.model.name || "Unknown"; } catch (e) { return "Unknown"; }
    },
    getLatestStockStatus() {
      try { return this.stock.latestStockStatus.stockStatus.name || "Unknown"; } catch (e) { return "Unknown"; }
    },
    getEtaDate() {
      try {
        const importData = this.stock.import || this.stock.Import;
        if (!importData) return "N/A";
        const eta = importData.estimateDateOfArrival || importData.EstimateDateOfArrival;
        return eta ? formatMalaysiaDate(eta) : "N/A";
      } catch (e) {
        return "N/A";
      }
    },
    getImageUrl(item, size = 256) {
      if (!item || !item.documentId) return "";
      return `${apiUrl}documents/Thumbnail/${item.documentId}?size=${size}`;
    },
    getFullImageUrl(item) {
      if (!item || !item.documentId) return "";
      return `${apiUrl}Documents/File/${item.documentId}`;
    },
    formatPrice(price) {
      if (!price || isNaN(price)) return "N/A";
      return new Intl.NumberFormat("en-MY", {
        style: "currency",
        currency: "MYR",
      }).format(price);
    },
    formatShortDate(d) {
      return moment(d).isValid() ? moment(d).format("DD MMM YYYY") : "—";
    },
    onPrint() {
      window.print();
    },
    toggleShareMenu() {
      this.shareMenuOpen = !this.shareMenuOpen;
    },
    closeShareMenu() {
      this.shareMenuOpen = false;
    },
    async onCopyLink() {
      const ok = await this._writeClipboard(window.location.href);
      if (ok) {
        this.copiedField = "link";
        if (this._copiedTimer) clearTimeout(this._copiedTimer);
        this._copiedTimer = setTimeout(() => {
          this.copiedField = null;
          this.closeShareMenu();
        }, 1200);
      } else {
        this.$emit("toast", { header: "Copy failed", message: "Couldn't copy link", color: "danger" });
      }
    },
    async copyToClipboard(text, fieldKey) {
      // Two-tier copy: prefer navigator.clipboard (modern, HTTPS-only),
      // fall back to a hidden textarea + execCommand for older browsers
      // (Android WebView, in-app browsers like the WhatsApp viewer often
      // don't expose the async clipboard API).
      const ok = await this._writeClipboard(text);
      if (ok) {
        this.copiedField = fieldKey;
        if (this._copiedTimer) clearTimeout(this._copiedTimer);
        this._copiedTimer = setTimeout(() => { this.copiedField = null; }, 1500);
        this.$emit("toast", { header: "Copied", message: "Copied to clipboard", color: "success" });
      } else {
        this.$emit("toast", { header: "Copy failed", message: "Couldn't copy to clipboard", color: "danger" });
      }
    },
    async _writeClipboard(text) {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
          return true;
        }
      } catch (e) { /* fall through to legacy path */ }
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
      } catch (e) {
        return false;
      }
    },
    onGoBack() {
      // Prefer Vue Router back, fall back to browser history
      if (this.$router && this.$router.go) this.$router.go(-1);
      else window.history.back();
    },
  },
  mounted() {
    window.addEventListener("keydown", this.handleKeydown);
  },
  destroyed() {
    window.removeEventListener("keydown", this.handleKeydown);
  },
};
</script>

<style scoped>
/* ========== Layout root ========== */
.vehicle-detail-page {
  background: #f5f6f8;
  min-height: 100vh;
  padding-bottom: 100px; /* room for the mobile sticky bottom bar */
}

/* ========== Sticky top action bar ========== */
.action-bar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 20px;
  background: #ffffff;
  border-bottom: 1px solid #e6e8ec;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
}
.action-bar-title {
  flex: 1;
  text-align: center;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2430;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.link-btn {
  background: transparent;
  border: 0;
  color: #2563eb;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
}
.link-btn:hover { background: #eff3ff; }
.action-btn {
  background: #1f2430;
  color: #ffffff;
  border: 0;
  padding: 10px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
}
.action-btn:hover { background: #2a3344; }
.action-btn-secondary {
  background: #ffffff;
  color: #1f2430;
  border: 1px solid #d1d5db;
}
.action-btn-secondary:hover { background: #f3f4f6; }

.action-bar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ========== Share menu ========== */
.share-wrap {
  position: relative;
  display: inline-flex;
}
.share-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 30;
  min-width: 200px;
  background: white;
  border: 1px solid #e6e8ec;
  border-radius: 10px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.10), 0 2px 6px rgba(0, 0, 0, 0.05);
  padding: 6px;
  display: flex;
  flex-direction: column;
}
.share-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  background: transparent;
  border: 0;
  color: #1f2430;
  font-size: 0.95rem;
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  text-decoration: none;
}
.share-item:hover { background: #f3f4f6; color: #111827; }
.share-item-icon {
  width: 20px;
  text-align: center;
  color: #6b7280;
  font-size: 1.05rem;
}
.share-item-icon-wa { color: #25d366; }
.share-item-icon-success { color: #047857; }

/* ========== Main 2-column layout ========== */
.main-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}
@media (min-width: 992px) {
  .main-layout {
    grid-template-columns: minmax(0, 1fr) 360px;
    gap: 32px;
    padding: 32px;
  }
}

.main-content {
  min-width: 0; /* prevent grid blowout from long content */
}

/* ========== Mobile header (above hero on < lg) ========== */
.mobile-header {
  background: white;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.stock-no-inline {
  color: #6b7280;
  font-size: 0.85rem;
  margin-left: 12px;
}
.vehicle-title-mobile {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin: 10px 0 6px;
  line-height: 1.2;
}

/* ========== Hero photo ========== */
.hero-section {
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: 16px;
}
.hero-image-wrapper {
  position: relative;
  aspect-ratio: 16 / 9;
  background: #e9ecef;
  cursor: pointer;
  overflow: hidden;
}
.hero-image-wrapper:hover .hero-overlay { opacity: 1; }
.hero-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}
.hero-image-wrapper:hover .hero-image { transform: scale(1.02); }
.hero-overlay {
  position: absolute;
  bottom: 16px;
  right: 16px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  opacity: 0.95;
  transition: opacity 0.2s ease;
  pointer-events: none;
}
.hero-placeholder {
  aspect-ratio: 16 / 9;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f1f3f5;
  color: #adb5bd;
}
.hero-placeholder i { font-size: 3rem; margin-bottom: 12px; }

/* ========== Thumbnail strip ========== */
.thumbnail-strip {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  padding: 4px 0 12px;
  margin-bottom: 16px;
  scrollbar-width: thin;
}
.thumb-item {
  flex: 0 0 88px;
  height: 66px;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  background: #e9ecef;
  scroll-snap-align: start;
  border: 2px solid transparent;
  transition: border-color 0.15s ease, transform 0.15s ease;
}
.thumb-item:hover {
  border-color: #2563eb;
  transform: translateY(-2px);
}
.thumb-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* ========== Section ========== */
.content-section {
  background: white;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}
.section-heading {
  font-size: 1.05rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 14px;
  letter-spacing: -0.01em;
}
.section-header .section-heading { margin: 0; }
.section-counter {
  background: #f1f3f5;
  color: #6b7280;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
}

/* ========== Specs grid ========== */
.specs-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
@media (min-width: 768px) {
  .specs-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (min-width: 1200px) {
  .specs-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}

/* Wide tiles span the full grid row regardless of breakpoint. Long
   identifiers like the 17-character chassis number plus a copy button
   wouldn't fit comfortably inside the standard 1/2- or 1/3-width tile,
   so we let the chassis no breathe across the whole row. The other
   wide-tile candidates (e.g. very long descriptions in a future spec)
   can opt in by adding `spec-tile-wide`. */
.spec-tile-wide { grid-column: 1 / -1; }
.spec-tile {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #f1f3f5;
}
.spec-icon {
  color: #2563eb;
  font-size: 1.1rem;
  width: 24px;
  text-align: center;
  flex-shrink: 0;
  margin-top: 2px;
}
.spec-content { min-width: 0; flex: 1; }
.spec-label {
  font-size: 0.72rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  margin-bottom: 2px;
}
.spec-value {
  font-size: 0.95rem;
  color: #111827;
  font-weight: 600;
  /* Only break very long unbreakable runs (e.g. chassis numbers handled below) */
}
.spec-value-mono {
  font-family: ui-monospace, SFMono-Regular, "Cascadia Mono", Menlo, Consolas, monospace;
  font-size: 0.85rem;
  /* Allow wrapping only if the value truly doesn't fit. `overflow-wrap:
     anywhere` breaks at any character when the container is too narrow
     but keeps the value on one line when it fits — unlike the previous
     `word-break: break-all` which always broke aggressively and made
     short chassis numbers look mangled on mobile. */
  overflow-wrap: anywhere;
  letter-spacing: 0.01em;
}

/* Chassis row: value + copy button side-by-side, value can shrink. */
.spec-value-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.spec-value-row .spec-value {
  min-width: 0;
  flex: 1 1 auto;
}
.copy-btn {
  flex: 0 0 auto;
  background: transparent;
  border: 1px solid #d1d5db;
  color: #4b5563;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  padding: 0;
}
.copy-btn:hover {
  background: #f3f4f6;
  color: #111827;
  border-color: #9ca3af;
}
.copy-btn:active { background: #e5e7eb; }
.copy-btn-success {
  background: #ecfdf5;
  color: #047857;
  border-color: #6ee7b7;
}
.copy-btn-success:hover {
  background: #d1fae5;
  color: #047857;
  border-color: #34d399;
}

/* ========== Description ========== */
.description-text {
  color: #374151;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0 0 12px;
  white-space: pre-line;
}

.ext-link {
  display: inline-flex;
  align-items: center;
  color: #2563eb;
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 500;
}
.ext-link:hover { text-decoration: underline; }

/* ========== Remarks ========== */
.remarks-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.remark-item {
  padding: 12px 14px;
  background: #f9fafb;
  border-left: 3px solid #2563eb;
  border-radius: 6px;
}
.remark-meta {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 0.85rem;
  color: #6b7280;
}
.remark-date { font-size: 0.78rem; }
.remark-content {
  color: #374151;
  font-size: 0.92rem;
  white-space: pre-wrap;
}
.empty-state {
  text-align: center;
  color: #9ca3af;
  font-size: 0.9rem;
  padding: 20px 0;
  margin: 0;
}

/* ========== Right sticky info aside (desktop) ========== */
.info-aside {
  position: sticky;
  top: 80px;
  align-self: start;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
}
.info-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #f1f3f5;
}

/* ========== Status pill ========== */
.status-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}
.status-pill {
  font-size: 0.72rem;
  padding: 5px 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-radius: 999px;
}
.status-pill-large {
  font-size: 0.78rem;
  padding: 7px 14px;
  display: inline-block;
}

.stock-no-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px dashed #e6e8ec;
}
.stock-no-label {
  font-size: 0.7rem;
  color: #9ca3af;
  letter-spacing: 0.08em;
  font-weight: 600;
}
.stock-no-value {
  font-size: 1rem;
  color: #111827;
  font-weight: 600;
}

.vehicle-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
  margin: 12px 0 6px;
  letter-spacing: -0.02em;
}
.vehicle-meta {
  color: #6b7280;
  font-size: 0.9rem;
}
.meta-dot { margin: 0 6px; color: #d1d5db; }

.price-block {
  margin: 20px 0;
  padding: 16px;
  background: linear-gradient(135deg, #f0f9ff 0%, #ecfdf5 100%);
  border-radius: 10px;
}
.price-label {
  font-size: 0.7rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
  margin-bottom: 4px;
}
.price-recommended {
  font-size: 1.8rem;
  font-weight: 800;
  color: #047857;
  letter-spacing: -0.02em;
  line-height: 1.1;
}
.price-min-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
.price-min-label {
  font-size: 0.72rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
}
.price-min-value {
  font-size: 1rem;
  color: #4b5563;
  font-weight: 600;
}

.primary-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 12px 16px;
  background: #1f2430;
  color: #ffffff;
  border: 0;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  min-height: 48px;
  transition: background 0.15s ease;
}
.primary-btn:hover { background: #2a3344; }

.info-meta {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f1f3f5;
}
.meta-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 4px 0;
}
.meta-label {
  font-size: 0.8rem;
  color: #6b7280;
}
.meta-value {
  font-size: 0.85rem;
  color: #111827;
  font-weight: 600;
}

/* ========== Mobile sticky bottom price bar ========== */
.mobile-price-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: white;
  border-top: 1px solid #e6e8ec;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.06);
}
.mpb-price { flex: 1; min-width: 0; }
.mpb-amount {
  font-size: 1.2rem;
  font-weight: 800;
  color: #047857;
  letter-spacing: -0.02em;
  line-height: 1.1;
}
.mpb-min {
  font-size: 0.78rem;
  color: #6b7280;
  margin-top: 2px;
}
.mpb-btn {
  background: #1f2430;
  color: white;
  border: 0;
  padding: 10px 16px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  min-height: 44px;
  white-space: nowrap;
}

/* ========== Lightbox modal — preserved from original ========== */
/* Lightbox: dark theme. CoreUI's default CModal background is white;
   we override the header / body / footer here so the photo is the focal
   point. The :deep() escape hatches into the CModal-rendered descendants
   from this scoped <style>. */
.image-modal-dark >>> .modal-content {
  background: #0b0b0d;
  color: #e5e7eb;
  border: 0;
  border-radius: 12px;
  overflow: hidden;
}
.image-modal-dark >>> .modal-header {
  background: #0b0b0d;
  border-bottom: 1px solid #1f2937;
  padding: 10px 16px;
}
.image-modal-dark >>> .modal-header .close,
.image-modal-dark >>> .modal-title {
  /* We render our own header — hide CModal's default chrome. */
  display: none;
}
.image-modal-dark >>> .modal-body {
  background: #0b0b0d;
  padding: 0;
}
.image-modal-dark >>> .modal-footer {
  /* Suppress the default Cancel/OK footer entirely. */
  display: none;
}
.image-modal-dark >>> .modal-dialog {
  max-width: min(1200px, 95vw);
}

/* Custom dark header */
.lightbox-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
}
.lightbox-header-title {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1 1 auto;
}
.lightbox-vehicle {
  color: #f9fafb;
  font-size: 1rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.lightbox-counter-chip {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.08);
  color: #e5e7eb;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.01em;
}
.lightbox-header-actions {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.lightbox-icon-btn {
  background: transparent;
  border: 0;
  color: #d1d5db;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.lightbox-icon-btn:hover { background: rgba(255, 255, 255, 0.08); color: #fff; }
.lightbox-icon-btn:active { background: rgba(255, 255, 255, 0.12); }

.modal-image-container {
  position: relative;
  text-align: center;
  background: #0b0b0d;
  /* Reserve a comfortable viewing area; the image scales to fit. */
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
}
.maximized-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  /* Smooth dim when a navigation is in flight so it's obvious the image
     is being replaced; pairs with the spinner overlay. */
  transition: opacity 0.18s ease-out;
}
.maximized-image.loading {
  opacity: 0.35;
}
.image-loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none; /* let arrow-button clicks pass through */
  background: rgba(0, 0, 0, 0.20);
}
.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.15);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  transition: background 0.15s ease, transform 0.15s ease;
}
.nav-button-prev { left: 16px; }
.nav-button-next { right: 16px; }
.nav-button:hover {
  background: rgba(255, 255, 255, 0.20);
  transform: translateY(-50%) scale(1.05);
}
.nav-button:active { background: rgba(255, 255, 255, 0.28); }
.image-loading {
  padding: 40px;
  color: #d1d5db;
}

/* Thumbnail filmstrip */
.lightbox-strip {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  background: #0b0b0d;
  border-top: 1px solid #1f2937;
  overflow-x: auto;
  /* Hide scrollbar but allow scrolling — keeps the strip looking clean. */
  scrollbar-width: thin;
  scrollbar-color: #374151 transparent;
}
.lightbox-strip::-webkit-scrollbar { height: 6px; }
.lightbox-strip::-webkit-scrollbar-thumb {
  background: #374151;
  border-radius: 3px;
}
.lightbox-thumb {
  flex: 0 0 auto;
  width: 72px;
  height: 54px;
  border-radius: 6px;
  border: 2px solid transparent;
  background: #1f2937;
  padding: 0;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.15s ease, transform 0.15s ease, opacity 0.15s ease;
  opacity: 0.65;
}
.lightbox-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.lightbox-thumb:hover {
  opacity: 1;
  transform: translateY(-2px);
}
.lightbox-thumb-active {
  border-color: #3b82f6;
  opacity: 1;
  box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.4);
}

/* Mobile: shrink nav buttons + thumbs so they fit comfortably. */
@media (max-width: 575px) {
  .nav-button { width: 40px; height: 40px; font-size: 1rem; }
  .nav-button-prev { left: 8px; }
  .nav-button-next { right: 8px; }
  .lightbox-vehicle { font-size: 0.9rem; }
  .lightbox-counter-chip { font-size: 0.72rem; padding: 2px 8px; }
  .modal-image-container { min-height: 50vh; }
  .maximized-image { max-height: 60vh; }
  .lightbox-thumb { width: 60px; height: 44px; }
}

/* ========== Print styles ========== */
@media print {
  /* Hide app chrome */
  :global(.c-sidebar),
  :global(.c-header),
  :global(.c-footer),
  :global(.c-app-loader),
  :global(footer) {
    display: none !important;
  }
  .action-bar,
  .mobile-price-bar,
  .thumbnail-strip,
  .ext-link,
  .primary-btn,
  .mpb-btn,
  .hero-overlay {
    display: none !important;
  }
  .vehicle-detail-page {
    background: white;
    padding: 0;
  }
  .main-layout {
    display: block;
    padding: 0;
    max-width: none;
  }
  .info-aside {
    position: static;
    display: block !important;
    max-height: none;
    overflow: visible;
    page-break-inside: avoid;
    margin-bottom: 16px;
  }
  .info-card,
  .content-section,
  .hero-section {
    box-shadow: none;
    border: 1px solid #e6e8ec;
    page-break-inside: avoid;
  }
  .hero-section {
    margin-bottom: 12px;
  }
  .hero-image-wrapper { cursor: default; aspect-ratio: 16 / 9; }
  .mobile-header { display: block !important; }
  .vehicle-title-mobile { font-size: 1.6rem; }
  body { font-size: 11pt; }
  /* Avoid wasting half the page on huge hero on print */
  .hero-image { max-height: 90mm; object-fit: contain; }
}
</style>
